import { FunctionComponent } from "react";
import { Button, Collapse, Form, Input, Pagination, Select, Tag, Typography } from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    FilterOutlined,
    LockOutlined,
    MailOutlined,
    PlusOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { UtilisateurViewProps } from "./types";
import { UserDto } from "../../../services/user";
import { getRoleLibelle, getRolesOptions } from "../../../components/common/commoninputs";

/** Génère les initiales prénom + nom (max 2 caractères). */
const getInitials = (prenom: string, nom: string): string => {
    const p = (prenom ?? "").trim()[0] ?? "";
    const n = (nom ?? "").trim()[0] ?? "";
    return (p + n).toUpperCase();
};

const AVATAR_COLORS = [
    "#2f54eb", "#13c2c2", "#52c41a", "#fa8c16",
    "#eb2f96", "#722ed1", "#1890ff", "#fa541c",
];

const getAvatarColor = (seed: string): string => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const UtilisateurMobileView: FunctionComponent<UtilisateurViewProps> = ({
    users,
    currentMobilePage,
    collapseActiveKey,
    onCreateUser,
    onEditUser,
    onDeleteUser,
    onResendActivationMail,
    fetchUsers,
    setCurrentMobilePage,
    setCollapseActiveKey,
}) => {
    const mobilePageSize = 10;

    const MobileUserCard = ({ user }: { user: UserDto }) => (
        <div className="utilisateur-card-mobile">
            {/* En-tête : avatar + identité */}
            <div className="utilisateur-card-header">
                <div
                    className="utilisateur-card-avatar"
                    style={{ backgroundColor: getAvatarColor(user.username) }}
                    aria-hidden="true"
                >
                    {getInitials(user.prenom, user.nom)}
                </div>
                <div className="utilisateur-card-identity">
                    <Typography.Text strong className="utilisateur-card-name">
                        {user.prenom} {user.nom}
                    </Typography.Text>
                    <Typography.Text type="secondary" className="utilisateur-card-username">
                        @{user.username}
                    </Typography.Text>
                </div>
                {/* Badges statut inline */}
                <div className="utilisateur-card-status-badges">
                    {user.enabled ? (
                        <Tag color="success" style={{ margin: 0 }}>Actif</Tag>
                    ) : (
                        <Tag color="error" style={{ margin: 0 }}>Inactif</Tag>
                    )}
                    {user.locked && (
                        <Tag color="error" icon={<LockOutlined />} style={{ margin: 0 }}>Verr.</Tag>
                    )}
                </div>
            </div>

            {/* Corps */}
            <div className="utilisateur-card-body">
                <div className="utilisateur-card-row">
                    <span className="utilisateur-card-label">Email</span>
                    <span className="utilisateur-card-value">{user.email}</span>
                </div>
                {user.mobile && (
                    <div className="utilisateur-card-row">
                        <span className="utilisateur-card-label">Mobile</span>
                        <span className="utilisateur-card-value">{user.mobile}</span>
                    </div>
                )}
                <div className="utilisateur-card-row">
                    <span className="utilisateur-card-label">Rôles</span>
                    <span className="utilisateur-card-value">
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {user.roles.map((r) => (
                                <Tag key={r.role} color="geekblue" style={{ margin: 0 }}>
                                    {getRoleLibelle(r)}
                                </Tag>
                            ))}
                        </div>
                    </span>
                </div>
            </div>

            {/* Actions */}
            <div className="utilisateur-card-actions">
                <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => onEditUser(user)}
                    style={{ color: "#597ef7", borderColor: "#597ef7" }}
                >
                    Modifier
                </Button>
                <Button
                    size="small"
                    icon={<MailOutlined />}
                    onClick={() => onResendActivationMail(user)}
                    disabled={user.enabled || !user.email}
                >
                    Mail
                </Button>
                <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onDeleteUser(user)}
                >
                    Supprimer
                </Button>
            </div>
        </div>
    );

    const SearchFilters = () => (
        <div>
            {[
                { name: "prenomFilter", libelle: "Prénom" },
                { name: "nomFilter", libelle: "Nom" },
                { name: "emailFilter", libelle: "E-mail" },
            ].map((f) => (
                <Form.Item key={f.name} name={f.name} label={f.libelle}>
                    <Input placeholder={f.libelle} onPressEnter={fetchUsers} />
                </Form.Item>
            ))}
            <Form.Item name="roleFilter" label="Rôle">
                <Select placeholder="Rôle" options={getRolesOptions()} allowClear />
            </Form.Item>
            <div className="centered-content">
                <Button
                    icon={<SearchOutlined />}
                    onClick={fetchUsers}
                    type="primary"
                    style={{ backgroundColor: "#597ef7", borderColor: "#597ef7" }}
                >
                    Rechercher
                </Button>
            </div>
        </div>
    );

    const filterCollapseItems = [
        {
            key: "1",
            label: (
                <span style={{ fontWeight: 500 }}>
                    <SearchOutlined style={{ marginRight: 6 }} />
                    Filtres de recherche
                </span>
            ),
            children: <SearchFilters />,
        },
    ];

    return (
        <>
            <Collapse
                activeKey={collapseActiveKey}
                onChange={(keys) => setCollapseActiveKey(keys as string[])}
                items={filterCollapseItems}
                className="mobile-filters-collapse utilisateur-filters-collapse"
            />

            {users && users.length > 0 ? (
                <div className="mobile-results">
                    <div className="utilisateur-results-header">
                        <Typography.Text strong style={{ color: "#fff", fontSize: 14 }}>
                            {users.length} utilisateur{users.length > 1 ? "s" : ""} trouvé{users.length > 1 ? "s" : ""}
                        </Typography.Text>
                        <Button
                            type="primary"
                            size="small"
                            onClick={onCreateUser}
                            icon={<PlusOutlined />}
                            style={{ backgroundColor: "#597ef7", borderColor: "#597ef7" }}
                        >
                            Nouveau
                        </Button>
                    </div>

                    <div className="utilisateur-cards-list">
                        {users
                            .slice(
                                (currentMobilePage - 1) * mobilePageSize,
                                currentMobilePage * mobilePageSize
                            )
                            .map((user) => (
                                <MobileUserCard key={user.id} user={user} />
                            ))}
                    </div>

                    <div className="mobile-pagination">
                        <Pagination
                            current={currentMobilePage}
                            pageSize={mobilePageSize}
                            total={users.length}
                            onChange={(page) => setCurrentMobilePage(page)}
                            showSizeChanger={false}
                            showTotal={(total, range) =>
                                `${range[0]}-${range[1]} sur ${total} utilisateurs`
                            }
                        />
                    </div>
                </div>
            ) : (
                <div className="no-results">
                    <FilterOutlined style={{ fontSize: 28, color: "#bfbfbf", display: "block", marginBottom: 8 }} />
                    Aucun utilisateur trouvé
                    <div style={{ marginTop: 12 }}>
                        <Button
                            type="primary"
                            onClick={onCreateUser}
                            icon={<PlusOutlined />}
                            style={{ backgroundColor: "#597ef7", borderColor: "#597ef7" }}
                        >
                            Créer un utilisateur
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};
